import i18n from "../i18n";
import ValidationResult from "../interfaces/api/validation-result";
import Gate from "../interfaces/robot/rooms/gate";
import RedZone from "../interfaces/robot/rooms/red-zone";
import { WaypointEdge, WaypointNode } from "../interfaces/robot/rooms/waypoints";
import { isPointInsidePolygon } from "./zone-validation";
import Point from "../interfaces/robot/rooms/point";

export const validateGateName = (name: string | null, takenNames: string[] | null = null): ValidationResult => {
  if (name == null || name.length === 0) {
    return { isValid: false, error: i18n.t("validation-error-gate-name-empty") };
  }

  if (name.length > 80) {
    return {
      isValid: false,
      error: i18n.t("validation-error-gate-name-too-long"),
    };
  }

  const specialChars = /[`!@#$%^&*()+=[\]{};':"\\|,.<>/?~]/;

  if (specialChars.test(name)) {
    return { isValid: false, error: i18n.t("validation-error-gate-name-includes-special-chars") };
  }

  if (takenNames != null && takenNames.some((n) => n === name)) {
    return { isValid: false, error: i18n.t("validation-error-gate-name-duplicate") };
  }

  return { isValid: true, error: null };
};

export const validateGateIdentifier = (
  identifier: string | null,
  takenIdentifiers: string[] | null = null,
): ValidationResult => {

  if (
    identifier == null ||
    (identifier !== "" &&
      takenIdentifiers != null && takenIdentifiers.some((n) => n === identifier))
  ) {
    return { isValid: false, error: i18n.t("gate-validation-error-repeated-identifier") };
  }

  return { isValid: true, error: null };
};

export const validateGate = (gate: Gate, redZone: RedZone | undefined, allGates: Gate[], waypointSet: {nodes: WaypointNode[], edges: WaypointEdge[]} ) => {
  const errors: ValidationResult[] = [];

  const nameValidation = validateGateName(
    gate.name,
    allGates.filter((g) => g.id !== gate.id && g.room === gate.room).map((g) => g.name),
  );
  nameValidation.inputKey = "name";
  if (!nameValidation.isValid) errors.push(nameValidation);

  const identifierValidation = validateGateIdentifier(
    gate.identifier,
    allGates.filter((g) => g.id !== gate.id).map((g) => g.identifier),
  );
  identifierValidation.inputKey = "identifier";
  if (!identifierValidation.isValid) errors.push(identifierValidation);

  if (!redZone){
    errors.push({ isValid: false, error: i18n.t("gate-validation-error-red-zone-does-not-exist")})
    return errors;
  } else {
    if (
      !isPointInsidePolygon(
        redZone.points.map((p) => p.point),
        gate.aPost,
      ) ||
      !isPointInsidePolygon(
        redZone.points.map((p) => p.point),
        gate.bPost,
      )
    ) {
      errors.push({ isValid: false, error: i18n.t("error-message-redZone-does-not-contain-gate") });
    }
  }

  if (arePointsClose(gate.aPost, gate.bPost, 1.2)){
    errors.push({ isValid: false, error: i18n.t("gate-validation-error-points-too-close")})
  }

  // add the id check to the backend validation
  const wayPointA = waypointSet.nodes.find(n => n.gateId === gate.id && n.id === gate.aWaypoint)
  const wayPointB = waypointSet.nodes.find(n => n.gateId === gate.id && n.id === gate.bWaypoint)
  const wayPointM = waypointSet.nodes.find(n => n.gateId === gate.id && n.id === gate.mWaypoint)

  if(!wayPointA || !wayPointB || !wayPointM){
    errors.push({ isValid: false, error: i18n.t("validation-error-gate-waypoints-missing") });
  } else{
    if (!arePointsOnOpposideSides(gate.aPost, gate.bPost, wayPointA.point, wayPointB.point)){
      errors.push({ isValid: false, error: i18n.t("validation-error-waypoints-opposite-sides-of-gate")});
    }
    if (isPointCloseToLine(gate.aPost, gate.bPost, wayPointA.point) || isPointCloseToLine(gate.aPost, gate.bPost, wayPointB.point)){
      errors.push({ isValid: false, error: i18n.t("gate-validation-error-wapoint-too-close-to-gate")});
    }
    if (isPointInsidePolygon(redZone.points.map((p) => p.point), wayPointA.point) || isPointInsidePolygon(redZone.points.map((p) => p.point), wayPointB.point)){
      errors.push({ isValid: false, error: i18n.t("validation-error-waypoints-outside-restricted-zone")});
    }
    const edgesIncidentToMWaypoint = waypointSet.edges.filter(e => e.u === wayPointM.id || e.v === wayPointM.id)
    if(edgesIncidentToMWaypoint.length !== 2){
      errors.push({ isValid: false, error: i18n.t("validation-error-mWaypoint-not-two-neighbors")});
    }
    const gateWaypointsConnected =
      edgesIncidentToMWaypoint.filter(e => e.u === wayPointA.id || e.v === wayPointA.id).length === 1 &&
      edgesIncidentToMWaypoint.filter(e => e.u === wayPointB.id || e.v === wayPointB.id).length === 1
    if(!gateWaypointsConnected){
      errors.push({ isValid: false, error: i18n.t("validation-error-gate-waypoints-not-connected")});
    }
    if(!isPointInTheMiddle(gate.aPost, gate.bPost, wayPointM.point)){
      errors.push({ isValid: false, error: i18n.t("validation-error-mWaypoint-not-in-center-of-gate")})
    }
  }
  return errors;
};

export const validateRoomGates = (roomId: string, allGates: Gate[], redZones: RedZone[], waypointSet: {nodes: WaypointNode[], edges: WaypointEdge[]}): ValidationResult[] =>{
  const roomGates = allGates.filter(g => g.room == roomId);
  return roomGates.flatMap(rg => validateGate(rg, redZones.find(rz => rz.gateId === rg.id), allGates, waypointSet))
};

const arePointsOnOpposideSides = (a: Point, b: Point, c: Point, d: Point): boolean => {
  const lineAB = (b.coordinateY - a.coordinateY) * (c.coordinateX - a.coordinateX) - (b.coordinateX - a.coordinateX) * (c.coordinateY - a.coordinateY);
  const lineABCD = (b.coordinateY - a.coordinateY) * (d.coordinateX - a.coordinateX) - (b.coordinateX - a.coordinateX) * (d.coordinateY - a.coordinateY);

  // Check if point c is on one side of line AB and point d is on the other side
  if ((lineAB > 0 && lineABCD < 0) || (lineAB < 0 && lineABCD > 0))
  {
      return true;
  }
  return false;
}

const isPointCloseToLine = (a: Point, b: Point, c: Point): boolean => {
  const minDistance = 0.5; // this could also be a parameter to enable function re-use (this should be meters... but I am feeling very unsure!)
  
  const dx = b.coordinateX - a.coordinateX;
  const dy = b.coordinateY - a.coordinateY;

  let closestX = a.coordinateX;
  let closestY = a.coordinateY;

  if (dx != 0 || dy != 0)
  // gate posts are NOT the same point
  {
      // Projection factor t
      let t = ((c.coordinateX - a.coordinateX) * dx + 
                  (c.coordinateY - a.coordinateY) * dy) / (dx * dx + dy * dy);

      // Clamp t to segment [0,1]
      t = Math.max(0, Math.min(1, t));

      // Closest point on segment
      closestX = a.coordinateX + t * dx;
      closestY = a.coordinateY + t * dy;
  }

  // Distance from C to closest point
  const distance = Math.sqrt(
      Math.pow(c.coordinateX - closestX, 2) +
      Math.pow(c.coordinateY - closestY, 2)
  );
  return distance < minDistance;
}

const arePointsClose = (a: Point, b: Point, minDistance: number): boolean => {
  const dx = b.coordinateX - a.coordinateX;
  const dy = b.coordinateY - a.coordinateY;
  
  const distance = Math.sqrt(dx * dx + dy * dy);
  
  return distance < minDistance;
}

const isPointInTheMiddle = (a: Point, b: Point, m: Point): boolean => {
  const halfPoint : Point = {
    coordinateX: (a.coordinateX + b.coordinateX) / 2,
    coordinateY: (a.coordinateY + b.coordinateY) / 2,
  };
  return arePointsClose(halfPoint, m, 0.02);
}